import * as Sentry from '@sentry/react'
import {AxiosResponse} from 'axios'
import {omit} from 'lodash'
import {create} from 'zustand'
import {createJSONStorage, persist} from 'zustand/middleware'
import {User, TokenRefresh, TokenObtainPair, EmployeeMe} from '../httpClient/__generated__/Api.ts'
import {apiClient} from '../httpClient/api.ts'
import {clearLocalDatabase} from '../db.ts'
import {LANG_STORAGE_KEY} from '../i18n/i18n.ts'

interface CredentialsStore {
    token: string | null
    refreshToken: string | null
    loading: boolean
    backUrl: string | null
    user?: User | null
    employee?: EmployeeMe | null
    view?: undefined | "employee" | "manager"
    setLoading: (loading: boolean) => void
    refreshPromise?: Promise<AxiosResponse<TokenRefresh>>
}

export const useCredentialsStore = create<CredentialsStore>()(
    persist<CredentialsStore>(
        set => ({
            setLoading: loading => set({loading}),
            user: null,
            backUrl: null,
            token: null,
            refreshToken: null,
            loading: true
        }),
        {
            storage: createJSONStorage(() => ({
                getItem: (key: string) => localStorage.getItem(key),
                setItem: (key: string, val: string) => localStorage.setItem(key, val),
                removeItem: (key: string) => localStorage.removeItem(key)
            })),
            name: 'crds',
            version: 3,
            // Bumped on every backend contract break: persisted user/employee objects of an
            // older version have a stale shape, so they are dropped and AuthLayer refetches
            // them. Tokens stay valid.
            migrate: (persisted, version) => {
                const state = persisted as CredentialsStore
                if (version < 3) {
                    return {...state, user: null, employee: null}
                }
                return state
            },
            partialize: s => {
                const {refreshPromise, ...state} = s
                return state
            },
            onRehydrateStorage: store => {
                return (state, error) => {
                    store?.setLoading(false)
                    if (state?.token) {
                        apiClient.instance.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
                    }
                }
            }
        }
    )
)

useCredentialsStore.subscribe(nextState => {
    if (nextState.loading) return
    apiClient.instance.defaults.headers.common['Authorization'] = nextState.token ? `Bearer ${nextState.token}` : ''
    Sentry.setUser({
        username: nextState.user?.username || '',
        id: nextState.user?.id?.toString()
    })
})

export const logOut = (backUrl: string | null = null) => {
    clearLocalDatabase()
    const lang = localStorage.getItem(LANG_STORAGE_KEY)
    localStorage.clear()
    if (lang) localStorage.setItem(LANG_STORAGE_KEY, lang)
    useCredentialsStore.setState({loading: false, backUrl, token: null, refreshToken: null})
}

export const loadEmployee = async () => {
    useCredentialsStore.setState({loading: true})
    const data = await apiClient.api.companyEmployeesMeRetrieve()
    if (data.data) {
        useCredentialsStore.setState({
            employee: omit(data.data),
        })
    }
    useCredentialsStore.setState({loading: false})
}

export const loadUser = async () => {
    useCredentialsStore.setState({loading: true})
    const data = await apiClient.api.authUsersMeRetrieve()
    if (data.data) {
        useCredentialsStore.setState({
            user: omit(data.data)
        })
        await loadEmployee()
    }
    useCredentialsStore.setState({loading: false})
}

export const logIn = async (username: string, password: string): Promise<{
    data: TokenObtainPair,
    rest: Omit<AxiosResponse<TokenObtainPair>, "data">
}> => {
    useCredentialsStore.setState({loading: true})
    const {data, ...rest} = await apiClient.api.jwtCreateCreate({
        username,
        password
    })
    if (data?.access && data?.refresh) {
        useCredentialsStore.setState({
            backUrl: null,
            loading: false,
            token: data.access,
            refreshToken: data.refresh
        })
    }
    return {data, rest}
}
